import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { CloudFunctionsService, isNetworkError } from './cloud-functions.service';
import { AuthService } from './auth.service';
import { CouchLoginReason } from './couch-login-storm';
import { LoggingService } from './logging.service';
import { IAPService, IAPProduct, BillingPlugin, buildStoreConfirmationFailure } from './iap.service';
import { isAppleCrossGradeScheduled, runStoreCrossGradeAsync } from './store-cross-grade-flow';
import { CoachTierCatalogEntry, CoachTierValidateParams, CoachTierValidateResult } from '@cf-shared/coach-billing-interface';
import { describeError } from '@cf-shared/error-describe';
import { DateTime } from 'luxon';
import {
  decidePendingCoachTierPurchase,
  forgetCoachTierPendingPurchase,
  forgetCoachTierPendingPurchaseIfSettled,
  isCoachTierPurchaseSettled,
  PendingCoachTierPurchaseAction,
  readCoachTierPendingPurchase,
  rememberCoachTierPurchase,
} from './coach-tier-pending-purchase';

/**
 * Client service for the Phase 2 fixed-tier (app-store) coach rail.
 *
 * Responsibilities:
 *  - load the per-env tier catalog from the server and pair each entry
 *    with its live store price (an `IAPProduct`);
 *  - run the initial tier purchase (delegates to
 *    `IAPService.purchaseCoachTierAsync`, which validates via
 *    `coachTierValidate` → writes `coachTierData`);
 *  - run an in-app tier upgrade / downgrade (cross-grade), reusing the
 *    same StoreKit2 / Google Play cross-grade machinery the personal
 *    subscription uses, but validating via `coachTierValidate`.
 *
 * Native-only by construction: the store rail does not exist on web (web
 * coaches use the metered PayPro flow). See
 * `design-docs/coach-slot-pricing.md`.
 */

/** One sellable tier: the catalog entry plus the resolved store product
 *  (price + offer token). `product` is null when the store did not
 *  return the product (e.g. dev placeholder ids, or a not-yet-approved
 *  product) — the UI shows the cap but disables the buy button. */
export interface CoachTierOption {
  entry: CoachTierCatalogEntry;
  product: IAPProduct | null;
}

export interface CoachTierActionResult {
  success: boolean;
  canceled?: boolean;
  error?: string;
  linkedEmail?: string;
  /** Apple downgrade only — the change was queued at end-of-cycle rather
   *  than applied immediately. */
  scheduled?: boolean;
  /** True when the store completed the purchase or the tier change and the
   *  failure recorded in `error` happened AFTER that, in our own confirmation
   *  step. The coach has been charged, so nothing shown to them may say it
   *  failed.
   *
   *  It does not, on its own, mean the tier will arrive without further help,
   *  and the two routes that set it recover by different means. When the SERVER
   *  answered that it could not reach the store, `coachTierValidate` parked a
   *  pending store claim and the next sign-in re-asks the store. When our own
   *  call to that endpoint THREW, the server was never told about the purchase,
   *  so the phone's own record of it is what comes back —
   *  `retryUnconfirmedPurchaseIfAnyAsync` below, and the already-owned re-check
   *  in `IAPService` for a coach whose device no longer holds that record. Both
   *  of those are best-effort, which is why the messages this flag selects say
   *  the plan MAY finish by itself (#5053) rather than promising it. See
   *  `design-docs/iap-subscriptions.md`, "The same rule, everywhere else".
   *
   *  Left unset when the store SETTLED the question, because there it really
   *  did not happen. */
  confirmationFailedAfterPurchase?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CoachTierService {
  // the env tier catalog is per-environment config, not per-user, so it
  // is safe to cache for the session after the first fetch
  private catalogCache: CoachTierCatalogEntry[] | null = null;

  constructor(
    private cloudFunctions: CloudFunctionsService,
    private iapService: IAPService,
    private auth: AuthService,
    private logging: LoggingService,
  ) {}

  /** The store product id for a tier on the CURRENT platform, or
   *  undefined when that store is not configured for this tier. iOS
   *  reads the Apple product id; Android reads the Google Play one. */
  private storeProductIdFor(entry: CoachTierCatalogEntry): string | undefined {
    return isPlatformIOS() ? entry.appleProductId : entry.googlePlayProductId;
  }

  /** Fetch + cache the env tier catalog. */
  private async getCatalogAsync(): Promise<CoachTierCatalogEntry[]> {
    if (this.catalogCache) return this.catalogCache;
    const { catalog } = await this.cloudFunctions.getCoachTierCatalogAsync({});
    this.catalogCache = catalog;
    return catalog;
  }

  /**
   * True when the CURRENT mobile store is configured for coach tiers —
   * i.e. the env catalog has at least one entry carrying a product id for
   * this platform. This is the per-store enablement gate: a store left
   * out of the catalog (e.g. Apple before its products are created) reads
   * as not-configured, and the UI hides the coach-tier entry there.
   * Always false on web/PWA (the tier rail is native-only; web uses the
   * metered rail). Returns false (hide) on a catalog-load failure rather
   * than surfacing a coach-tier entry that can't function.
   */
  async isTierConfiguredForPlatformAsync(): Promise<boolean> {
    if (!isCapacitorNative()) return false;
    try {
      const catalog = await this.getCatalogAsync();
      return catalog.some(e => this.storeProductIdFor(e) !== undefined);
    } catch (error) {
      this.logging.trackSilentProblem('CoachTierCatalogLoadFailed', { errorMessage: describeError(error) });
      return false;
    }
  }

  /**
   * Load the tier catalog and pair each entry with its live store price.
   * Returns the tiers in catalog (display) order. Entries whose product
   * is not configured for the current platform (or that the store does
   * not return) have `product: null`.
   */
  async loadTiersAsync(): Promise<CoachTierOption[]> {
    const catalog = await this.getCatalogAsync();
    if (catalog.length === 0) return [];

    // resolve store prices for this platform's configured product ids only
    let products: IAPProduct[] = [];
    if (isCapacitorNative()) {
      const productIds = catalog
        .map(e => this.storeProductIdFor(e))
        .filter((id): id is string => id !== undefined);
      if (productIds.length > 0) {
        products = await this.iapService.getProductsAsync(productIds);
      }
    }
    const byId = new Map<string, IAPProduct>(products.map(p => [p.id, p]));

    return catalog.map(entry => {
      const productId = this.storeProductIdFor(entry);
      return {
        entry,
        product: productId ? byId.get(productId) ?? null : null,
      };
    });
  }

  /**
   * Buy a tier for the first time. Delegates to the coach-tier purchase
   * path so the receipt is validated into `coachTierData` (not the
   * personal block).
   */
  async purchaseTierAsync(option: CoachTierOption): Promise<CoachTierActionResult> {
    if (!option.product) {
      return { success: false, error: 'tier_product_unavailable' };
    }
    return this.iapService.purchaseCoachTierAsync(option.product);
  }

  /**
   * Finish a coach-tier purchase the store completed and our server never heard
   * about, if this device is holding one.
   *
   * The case it exists for: the store took the money, and our confirmation call
   * threw before it reached the server — a missing sign-in token, a dropped
   * connection. Nothing else recovers that coach. The server parked no claim,
   * because it was never told; a sign-in re-checks only the personal
   * subscription blocks; and the store's own notification is matched by a
   * coach-tier token this coach does not have yet. So the phone wrote the
   * purchase down at the moment it was charged, and this re-sends it.
   *
   * Silent by design. It shows no dialog on success or on failure: the coach was
   * already told at the time that their plan may finish activating, and the
   * plan simply appearing is what that promised. The Subscribe page's
   * `checkExistingStorePurchase` behaves the same way for the personal rail.
   *
   * Costs one `localStorage` read for everybody who has no record, which is
   * everybody almost always. Safe to call more than once; safe to call on web,
   * where there is never a record to find.
   *
   * @returns true only when this call granted the tier, so a caller that was
   *          about to offer the coach a plan can stop and say they have one. The
   *          app-start caller ignores it; the become-a-coach flow does not.
   */
  async retryUnconfirmedPurchaseIfAnyAsync(): Promise<boolean> {
    const record = readCoachTierPendingPurchase();
    if (!record) return false;

    const action = decidePendingCoachTierPurchase(record, this.auth.user?.uid, DateTime.utc());
    if (action === PendingCoachTierPurchaseAction.DropExpired) {
      // we never managed to record this purchase. Worth seeing, but read it as
      // "look at this one", not as "a coach is stranded": the same limit also
      // ends a purchase the store reversed days ago, because that answer arrives
      // as a sentence rather than a code and so never settles the record. Which
      // of the two it was is in the refusals reported under
      // CoachTierPendingPurchaseRefused for the same coach.
      forgetCoachTierPendingPurchase(record.uid);
      this.logging.trackSilentProblem('CoachTierPendingPurchaseExpired', {
        provider: record.params.provider,
        rememberedAt: record.rememberedAt_utc,
      });
      return false;
    }
    // Nothing to do for a record belonging to another account that used this
    // device: it is not ours to send, and not ours to delete either.
    if (action !== PendingCoachTierPurchaseAction.Retry) return false;

    try {
      // exactly the request the purchase made, replayed
      const validation = await this.cloudFunctions.coachTierValidateAsync(record.params);
      const settled = isCoachTierPurchaseSettled(validation.error);
      if (settled) forgetCoachTierPendingPurchase(record.uid);

      if (validation.error) {
        this.logging.trackSilentProblem('CoachTierPendingPurchaseRefused', { error: validation.error, settled });
        return false;
      }
      // the tier is recorded now, and the repair is proven by the answer above
      this.logging.trackSilentInfo('CoachTierPendingPurchaseHealed', { provider: record.params.provider });
      await this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachTierPendingRetry);
      return true;
    } catch (error) {
      // KEEP the record. A throw means nobody has answered about this purchase
      // yet, which is the very situation it was written down for; the next app
      // open asks again, until the record ages out.
      const detail = { errorMessage: describeError(error) };
      if (isNetworkError(error)) {
        this.logging.trackSilentInfo('CoachTierPendingPurchaseRetryOffline', detail);
      } else {
        this.logging.trackSilentProblem('CoachTierPendingPurchaseRetryFailed', detail);
      }
      return false;
    }
  }

  /**
   * Upgrade or downgrade to a different tier (cross-grade). Reuses the
   * store cross-grade primitive (StoreKit2 same-group on iOS;
   * `oldPurchaseToken`-based replacement on Google Play) and validates
   * the result through `coachTierValidate`.
   */
  async changeTierAsync(option: CoachTierOption): Promise<CoachTierActionResult> {
    if (!isCapacitorNative()) {
      return { success: false, error: 'tier_change_native_only' };
    }
    if (!option.product) {
      return { success: false, error: 'tier_product_unavailable' };
    }

    try {
      if (Capacitor.getPlatform() === 'ios') {
        return await this.changeTierAppleAsync(option.product);
      }
      return await this.changeTierGoogleAsync(option.product);
    } catch (error) {
      // only a throw from BEFORE the store handed over a purchase token reaches
      // here — each changeTier* method answers its own later throws, because by
      // then the store has made the change and this answer would be false. So
      // there is no evidence anything happened, and the terminal wording the
      // page shows for it is correct.
      this.logging.trackSilentProblem('CoachTierChangeFailed', { errorMessage: describeError(error) });
      return { success: false, error: describeError(error) };
    }
  }

  // Apple same-group cross-grade. offerToken / oldPurchaseToken are
  // Android-only and ignored by the iOS native side. The order of the steps, and
  // when the coach counts as charged, live in runStoreCrossGradeAsync; this
  // supplies only what is Apple- and coach-tier-specific. Unlike the personal
  // plan switch, a throw from the session refresh is not swallowed, so it comes
  // back as the charged-but-unconfirmed answer.
  private async changeTierAppleAsync(product: IAPProduct): Promise<CoachTierActionResult> {
    // built in one place, so the request we send and the one we write down for a
    // later retry cannot drift apart
    const appleParams = (response: { purchaseToken: string; orderId?: string }): CoachTierValidateParams => ({
      provider: 'apple',
      transactionId: response.purchaseToken,
      originalTransactionId: response.orderId || response.purchaseToken,
    });

    return runStoreCrossGradeAsync<CoachTierValidateResult>({
      runStoreCallAsync: () => BillingPlugin.updateSubscription({
        productId: product.id,
        offerToken: '',
        oldPurchaseToken: '',
      }),
      reportUnverifiedOnDevice: response => {
        this.logging.trackSilentProblem('CoachTierChangeUnverifiedOnDevice', { productId: product.id, reason: response.unverifiedReason });
      },
      rememberUnconfirmedPurchase: response => this.rememberUnconfirmedTierChange(appleParams(response)),
      onServerAnswered: validation => forgetCoachTierPendingPurchaseIfSettled(this.auth.user?.uid, validation.error),
      validateAsync: response => this.cloudFunctions.coachTierValidateAsync(appleParams(response)),
      buildValidationFailure: buildStoreConfirmationFailure,
      isScheduled: validation => isAppleCrossGradeScheduled(validation, product.id),
      finishAsync: () => this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachTierChange),
    });
  }

  // Google Play cross-grade: pass the current coach-tier purchase token as
  // oldPurchaseToken so Google replaces the existing subscription.
  private async changeTierGoogleAsync(product: IAPProduct): Promise<CoachTierActionResult> {
    const oldPurchaseToken = this.auth.couch?.coachTierData?.purchaseToken;
    if (!oldPurchaseToken) {
      return { success: false, error: 'No existing coach tier subscription found.' };
    }

    // built in one place, so the request we send and the one we write down for a
    // later retry cannot drift apart
    const googlePlayParams = (
      response: { purchaseToken: string; productId?: string },
      accountId: string | undefined,
    ): CoachTierValidateParams => ({
      provider: 'googlePlay',
      purchaseToken: response.purchaseToken,
      productId: response.productId || product.id,
      basePlanId: product.basePlanId,
      obfuscatedAccountId: accountId,
    });

    // same shared flow as the Apple method above.
    return runStoreCrossGradeAsync<CoachTierValidateResult>({
      getAccountIdAsync: () => this.iapService.getObfuscatedAccountId(),
      runStoreCallAsync: accountId => BillingPlugin.updateSubscription({
        productId: product.id,
        offerToken: product.offerToken,
        oldPurchaseToken,
        accountId,
      }),
      // the Android plugin never sends this today; reported rather than dropped
      // in case it ever does.
      reportUnverifiedOnDevice: response => {
        this.logging.trackSilentProblem('CoachTierChangeUnverifiedOnDevice', { productId: product.id, reason: response.unverifiedReason });
      },
      // Google issues a NEW token for the changed plan, and the store notification
      // for it matches no account until our server has recorded it. So if the
      // call below throws, this record is the only way the change gets recorded
      // (#5611).
      rememberUnconfirmedPurchase: (response, accountId) =>
        this.rememberUnconfirmedTierChange(googlePlayParams(response, accountId)),
      onServerAnswered: validation => forgetCoachTierPendingPurchaseIfSettled(this.auth.user?.uid, validation.error),
      validateAsync: (response, accountId) =>
        this.cloudFunctions.coachTierValidateAsync(googlePlayParams(response, accountId)),
      buildValidationFailure: buildStoreConfirmationFailure,
      finishAsync: () => this.auth.attemptCouchLoginAsync(CouchLoginReason.CoachTierConfirm),
    });
  }

  /**
   * Write a tier change down on the device before our server is told anything.
   *
   * The same record, and the same retry, as a first purchase
   * (`IAPService.purchaseCoachTierAsync`): `retryUnconfirmedPurchaseIfAnyAsync`
   * re-sends it at the next app open. Never throws — losing the record must not
   * stop the confirmation that follows, but we want to know it happened.
   */
  private rememberUnconfirmedTierChange(params: CoachTierValidateParams): void {
    const uid = this.auth.user?.uid;
    if (!uid) return; // nothing to attach it to; the retry refuses it anyway
    const remembered = rememberCoachTierPurchase({ uid, rememberedAt_utc: DateTime.utc().toISO()!, params });
    if (!remembered.stored) {
      this.logging.trackSilentProblem('CoachTierPendingPurchaseNotStored', { provider: params.provider, errorMessage: describeError(remembered.error) });
    }
  }
}
